import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AccountLayout } from '@components/layout/AccountLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import { Button } from '@components/ui/Button';
import { ClientDrawer, isNewClient, useClients, useCreateClient, useUpdateClient } from '@features/clients';
import type { ClientListItem, CreateClientRequest } from '@features/clients';
import { useDebounce } from '@hooks/useDebounce';
import { cn } from '@/styles/tailwind-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const AVATAR_COLORS = [
  'bg-violet-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-orange-500',
  'bg-cyan-600',
  'bg-lime-600',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? 'bg-gray-500';
}

function clientLabel(client: ClientListItem, now: Date): 'new' | 'active' | 'inactive' {
  if (client.status === 'INACTIVE') return 'inactive';
  if (isNewClient(client, now)) return 'new';
  return 'active';
}

/**
 * Wraps every case-insensitive occurrence of `query` within `text` in a
 * highlighted <mark>, so the user can see which values matched their search.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === q.toLowerCase() ? (
      <mark key={i} className="rounded-sm bg-yellow-200/80 text-inherit">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function ClientAvatar({ firstName, lastName }: { firstName: string; lastName: string }) {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const color = avatarColor(`${firstName}${lastName}`);
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white',
        color,
      )}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function ClientBadge({ label }: { label: 'new' | 'active' | 'inactive' }) {
  const { t } = useTranslation();
  const styles = {
    active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    new: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    inactive: 'bg-gray-100 text-gray-500 ring-gray-400/20',
  };
  const labels = {
    active: t('clients.status.active'),
    new: t('clients.status.new'),
    inactive: t('clients.status.inactive'),
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        styles[label],
      )}
    >
      {label === 'new' && <span className="mr-1">+</span>}
      {labels[label]}
    </span>
  );
}

// ─── Contact icons ──────────────────────────────────────────────────────────

const IconMail = () => (
  <svg
    className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="1.5" y="3" width="13" height="10" rx="1.5" />
    <path d="M2 4l6 4.5L14 4" />
  </svg>
);

const IconPhone = () => (
  <svg
    className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5.5 2.5l1 2.5-1.5 1.5a8 8 0 003.5 3.5L11 8.5l2.5 1v2.5a1 1 0 01-1.1 1A11 11 0 012 3.1 1 1 0 013 2h2.5z" />
  </svg>
);

// ─── Client row ───────────────────────────────────────────────────────────────

interface ClientRowProps {
  client: ClientListItem;
  now: Date;
  search: string;
  onClick?: () => void;
}

function ClientRow({ client, now, search, onClick }: ClientRowProps) {
  const label = clientLabel(client, now);

  return (
    <div
      className="group flex cursor-pointer items-center gap-4 border-b border-border/60 px-5 py-3.5 last:border-b-0 hover:bg-muted/30 transition-colors"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      {/* Avatar */}
      <ClientAvatar firstName={client.firstName} lastName={client.lastName} />

      {/* Name + meta */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-foreground">
            {highlightMatch(`${client.firstName} ${client.lastName}`, search)}
          </span>
          <ClientBadge label={label} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
          {client.email && (
            <span className="flex min-w-0 items-center gap-1.5">
              <IconMail />
              <span className="truncate">{highlightMatch(client.email, search)}</span>
            </span>
          )}
          {client.phone && (
            <span className="flex items-center gap-1.5">
              <IconPhone />
              <span>{highlightMatch(client.phone, search)}</span>
            </span>
          )}
        </div>
      </div>

      {/* Right side: service tag placeholder + appointment placeholder */}
      <div className="hidden items-center gap-2 sm:flex">
        {/* Service tag placeholder */}
        <div className="h-6 w-24 animate-none rounded-full bg-muted/50" />

        {/* Appointment date placeholder */}
        <div className="flex items-center gap-1 rounded-full border border-border bg-white px-2.5 py-1">
          <svg
            className="h-3 w-3 text-muted-foreground/50"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <rect x="2" y="3" width="12" height="11" rx="1.5" />
            <path d="M5 1.5v3M11 1.5v3M2 7h12" />
          </svg>
          <div className="h-2 w-14 rounded-full bg-muted/50" />
        </div>
      </div>

      {/* Chevron */}
      <svg
        className="h-4 w-4 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M6 4l4 4-4 4" />
      </svg>
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ClientsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allClients, metrics, isLoading, error } = useClients();
  const { mutate: createClient, isPending: isCreating, error: createError, reset: resetCreate } = useCreateClient();
  const { mutate: updateClient, isPending: isUpdating, error: updateError, reset: resetUpdate } = useUpdateClient();

  const [searchRaw, setSearchRaw] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerClient, setDrawerClient] = useState<ClientListItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const search = useDebounce(searchRaw, 300);
  const now = useMemo(() => new Date(), []);

  const isSubmitting = isCreating || isUpdating;

  const drawerErrorMessage = useMemo(() => {
    const err = drawerClient ? updateError : createError;
    if (!err) return null;
    switch (err.code) {
      case 'EMAIL_ALREADY_EXISTS':
        return t('clients.errors.emailAlreadyExists');
      case 'CNP_ALREADY_EXISTS':
        return t('clients.errors.cnpAlreadyExists');
      default:
        return drawerClient
          ? t('clients.errors.updateFailed')
          : t('clients.errors.createFailed');
    }
  }, [drawerClient, createError, updateError, t]);

  function handleOpenAddDrawer() {
    resetCreate();
    resetUpdate();
    setDrawerClient(null);
    setIsDrawerOpen(true);
  }

  function handleOpenEditDrawer(client: ClientListItem) {
    resetCreate();
    resetUpdate();
    setDrawerClient(client);
    setIsDrawerOpen(true);
  }

  function handleCloseDrawer() {
    setIsDrawerOpen(false);
  }

  function handleDrawerSubmit(data: CreateClientRequest) {
    if (drawerClient) {
      updateClient(
        { clientId: drawerClient.clientId, request: data },
        { onSuccess: () => setIsDrawerOpen(false) },
      );
    } else {
      createClient(data, { onSuccess: () => setIsDrawerOpen(false) });
    }
  }

  const filteredClients = useMemo<ClientListItem[]>(() => {
    if (!search.trim()) return allClients;
    const q = search.toLowerCase();
    return allClients.filter(
      (c) =>
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.phone.includes(q),
    );
  }, [allClients, search]);

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageClients = filteredClients.slice(pageStart, pageStart + PAGE_SIZE);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchRaw(e.target.value);
    setCurrentPage(1);
  }

  return (
    <AccountLayout>
      <div
        className="min-h-[calc(100vh-4rem)]"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 80% 0%, hsl(160 51% 24% / 0.06) 0%, transparent 70%), hsl(var(--color-background))',
        }}
      >
        <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-10">

          {/* ── Header ── */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-2xl font-semibold text-foreground">
                {t('clients.title')}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {isLoading
                  ? t('clients.subtitleLoading')
                  : t('clients.subtitle', { count: metrics.total })}
              </p>
            </div>
            <Button size="sm" onClick={handleOpenAddDrawer} leftIcon={
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M7 1v12M1 7h12" />
              </svg>
            }>
              {t('clients.addClient')}
            </Button>
          </div>

          {/* ── Search bar ── */}
          <div className="mt-6">
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                <IconSearch />
              </span>
              <input
                type="search"
                value={searchRaw}
                onChange={handleSearchChange}
                placeholder={t('clients.searchPlaceholder')}
                className="h-11 w-full rounded-xl border border-border bg-white/80 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {/* ── Client list card ── */}
          <div className="mt-3 overflow-hidden rounded-md border border-border bg-white/70 shadow-sm">

            {/* List body */}
            {isLoading ? (
              <BrandLoader
                title={t('clients.loading.title')}
                subtitle={t('clients.loading.subtitle')}
              />
            ) : error ? (
              <div className="px-5 py-12 text-center">
                <p className="text-sm text-destructive">{t('clients.errors.loadFailed')}</p>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-5 py-16 text-center">
                <svg className="mb-3 h-10 w-10 text-muted-foreground/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
                <p className="text-sm font-medium text-foreground">
                  {search ? t('clients.empty.searchTitle') : t('clients.empty.title')}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {search ? t('clients.empty.searchDesc') : t('clients.empty.desc')}
                </p>
              </div>
            ) : (
              pageClients.map((client) => (
                <ClientRow
                  key={client.clientId}
                  client={client}
                  now={now}
                  search={search}
                  onClick={() =>
                    navigate(`/account/clients/${client.clientId}/view`, { state: { client } })
                  }
                />
              ))
            )}

            {/* Footer: pagination info + controls */}
            {!isLoading && !error && filteredClients.length > 0 && (
              <div className="flex items-center justify-between border-t border-border px-5 py-3">
                <p className="text-xs text-muted-foreground">
                  {t('clients.showing', {
                    from: pageStart + 1,
                    to: Math.min(pageStart + PAGE_SIZE, filteredClients.length),
                    total: filteredClients.length,
                  })}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-default"
                      aria-label={t('clients.prevPage')}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 10L6 7l3-3" />
                      </svg>
                    </button>
                    <span className="px-1 text-xs text-muted-foreground">
                      {safePage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-default"
                      aria-label={t('clients.nextPage')}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 4l3 3-3 3" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
      <ClientDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        client={drawerClient}
        onSubmit={handleDrawerSubmit}
        isSubmitting={isSubmitting}
        errorMessage={drawerErrorMessage}
      />
    </AccountLayout>
  );
}
